import cookie from 'js-cookie';
import { FC, lazy, Suspense, useEffect, useMemo } from 'react';
import { isMobileOnly } from 'react-device-detect';
import { ErrorBoundary } from 'react-error-boundary';
import { useQuery } from 'react-query';
import { Outlet } from 'react-router-dom';

import Button from '@/components/elements/Button/BaseButton';
import { useLayoutContext } from '@/contexts/layout';
import { useUserContext } from '@/contexts/user';
import useModal from '@/hooks/modal/useModal';
import DmmNav from '@/layouts/DmmNav';
import Footer from '@/layouts/Footer';
import Nav from '@/layouts/Nav';
import {
  ErrorCode,
  RaceProgram,
  useGetCountryCodeLazyQuery,
  useGetRaceProgramsLazyQuery,
} from '@/lib/apollo/types/gen/graphql';
import { dayjst } from '@/lib/dayjst';
import { client } from '@/lib/microcms';
import ErrorPage from '@/pages/Error';
import { NewsType } from '@/pages/News/types';

import { ButtonWrapper, Content, Wrapper } from './style';

const CurrentRaceListModal = lazy(
  () => import('@/components/layouts/Modal/CurrentRaceList'),
);

const QuickChargeModal = lazy(
  () => import('@/components/layouts/Modal/Order/QuickCharge'),
);

const LayoutErrorBoundary: FC = ({ children }) => {
  return (
    <ErrorBoundary
      fallbackRender={() => (
        <ErrorPage forceErrorCode={ErrorCode.InternalError} />
      )}
    >
      {children}
    </ErrorBoundary>
  );
};

const Layout: FC = () => {
  const { customStyles, showBetButton } = useLayoutContext();

  const [isOpenQuickChargeModal, setIsOpenQuickChargeModal] = useModal(false);
  const [isOpenCurrentRaceListModal, setIsOpenCurrentRaceListModal] =
    useModal(false);
  const [isOpenDrawer, setIsOpenDrawer] = useModal(false);
  const { isMember, isDisabledMember } = useUserContext();

  const date = useMemo(() => dayjst(), []);

  const nowISO = dayjst().toISOString();
  const dateString = dayjst().format('YYYY-MM-DD');
  const { data: campaigns } = useQuery({
    queryKey: ['current_campaign_top', dateString],
    queryFn: async () =>
      client.getContents(NewsType.Campaign, {
        limit: 100,
        fields: 'id,raceProgramInfo',
        filters: `endTime[greater_than]${nowISO}[and]startTime[less_than]${nowISO}`,
      }),
  });

  const [loadCountryCode, countryCodeQuery] = useGetCountryCodeLazyQuery();

  const [loadRacePrograms, { data: rpData, loading: isLoadingRP }] =
    useGetRaceProgramsLazyQuery({
      variables: {
        input: {
          heldDate: date,
        },
      },
    });

  useEffect(() => {
    if (isOpenCurrentRaceListModal) {
      loadRacePrograms();
    }
  }, [isOpenCurrentRaceListModal]);

  useEffect(() => {
    const ckcyVal = cookie.get('ckcy');
    if (typeof ckcyVal === 'undefined') {
      // 「undefined: 未定義」の場合は国コードを再取得する
      loadCountryCode();
      return;
    } else if (ckcyVal === '2') {
      // 利用できませんのページにリダイレクトする
      document.location =
        'https://special.dmm.com/not-available-in-your-region/allservice';
    }

    const ckcySaveDate = cookie.get('ckcySaveDate');
    if (ckcySaveDate) {
      const resetBaseTime = dayjst().add(-7, 'day').format('YYYY-MM-DD'); // 7日前の日付
      if (resetBaseTime > ckcySaveDate) {
        // 七日以上経過してたら再取得する → 要確認
        loadCountryCode();
        return;
      }
    }
  }, []);

  useEffect(() => {
    if (countryCodeQuery.data) {
      const nowIso = dayjst().format('YYYY-MM-DD'); //現在日時
      const options: cookie.CookieAttributes = {
        expires: 365,
        domain: '.dmm.com',
        path: '/',
        secure: false,
      };

      var ckcyVal = '2';

      if (
        countryCodeQuery.data.countryCode.isSearchEngine ||
        countryCodeQuery.data.countryCode.isSns ||
        countryCodeQuery.data.countryCode.isOgp
      ) {
        ckcyVal = '0';
      } else if (countryCodeQuery.data.countryCode.code === 'JP') {
        ckcyVal = '1';
      }

      cookie.set('ckcy', ckcyVal, options);
      cookie.set('ckcySaveDate', nowIso, options);

      if (ckcyVal === '2') {
        // 利用できませんのページにリダイレクトする
        document.location =
          'https://special.dmm.com/not-available-in-your-region/allservice';
      }
    }
  }, [countryCodeQuery]);

  return (
    <>
      {isMember && (
        <Suspense fallback={<></>}>
          <QuickChargeModal
            isOpen={isOpenQuickChargeModal}
            handleClose={() => setIsOpenQuickChargeModal(false)}
          />
        </Suspense>
      )}
      <Suspense fallback={<></>}>
        <CurrentRaceListModal
          racePrograms={(rpData?.racePrograms || []) as RaceProgram[]}
          campaigns={campaigns}
          isOpen={isOpenCurrentRaceListModal}
          isLoading={isLoadingRP}
          handleClose={() => setIsOpenCurrentRaceListModal(false)}
        />
      </Suspense>
      <Wrapper>
        <DmmNav isMobile={isMobileOnly}>
          <LayoutErrorBoundary>
            {!isDisabledMember && (
              <Nav
                handleOpenQuickChargeModal={() =>
                  setIsOpenQuickChargeModal(true)
                }
                isOpenDrawer={isOpenDrawer}
                setIsOpenDrawer={setIsOpenDrawer}
              />
            )}
            <LayoutErrorBoundary>
              <Content customStyles={customStyles}>
                <Suspense fallback={<></>}>
                  <Outlet />
                </Suspense>
                {showBetButton && (
                  <ButtonWrapper>
                    <Button
                      size="xxl"
                      bgColor="primary"
                      radius="circle"
                      fontWeight="bold"
                      width="112px"
                      height="112px"
                      onClick={() => setIsOpenCurrentRaceListModal(true)}
                    >
                      投票
                    </Button>
                  </ButtonWrapper>
                )}
              </Content>
            </LayoutErrorBoundary>
          </LayoutErrorBoundary>
        </DmmNav>
        <Footer />
      </Wrapper>
    </>
  );
};

export default Layout;
